import { errorMessage } from "@api/errors";
import {
  measurementsTrialHistory,
  useMeasurementsHistoryPresets,
  useMeasurementsOverview,
  useMeasurementsVisualTraits,
} from "@api/generated/measurements/measurements";
import { type FieldSitesIn, type HistoryIn, HistoryInMethod, HistoryInSiteMode } from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { listParam } from "@app/search";
import { hasRole, useSaveSetting, useSeasonFromLink, useSettings } from "@app/workspace";
import { FieldSitesPill } from "@features/trials/FieldSitesPill";
import { HistoryTable, useHistoryTable } from "@features/trials/HistoryTable";
import { ParallelCopyModal } from "@features/trials/ParallelCopyModal";
import { HistoryVisual } from "@features/visualization/HistoryVisual";
import { Button, Checkbox, Input, Loader, SegmentedControl, Select, TagsInput } from "@mantine/core";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ActionMenu } from "@ui/ActionMenu";
import { formatNumber } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const METHODS = Object.values(HistoryInMethod);
const SITE_MODES = Object.values(HistoryInSiteMode);

const search = z.object({
  ev: z.number().int().optional(),
  view: z.enum(["overview", "table"]).default("overview"),
  trait: z.string().default("trms.atlag"),
  relative: z.boolean().default(false),
  kis: z.string().optional(),
  method: z.enum(HistoryInMethod).default("e"),
  depth: z.number().int().min(0).default(1), // clamped to the method's deepest history (presets)
  site_mode: z.enum(HistoryInSiteMode).default("avg"),
  th: listParam().default([]),
  fields: z.array(z.string()).max(60).optional(),
  // a property's own sites and mode (the legacy Yearly Retriever's sites of a selected field)
  field_sites: z.record(z.string(), z.object({ sites: z.array(z.string()), mode: z.enum(HistoryInSiteMode) })).default({}),
  pedigree: z.boolean().default(true),
  valid: z.boolean().default(false),
  unlinked: z.boolean().default(false),
});

export const Route = createFileRoute("/trials/yearly-retriever")({ validateSearch: search, component: YearComparison });

function YearComparison() {
  const { t } = useTranslation();
  const params = Route.useSearch();
  const traits = useMeasurementsVisualTraits().data ?? [];
  const navigate = useNavigate({ from: Route.fullPath });
  const ev = useSeasonFromLink(params.ev);
  const settings = useSettings();
  const saveSetting = useSaveSetting();
  const presets = useMeasurementsHistoryPresets({ query: { staleTime: Number.POSITIVE_INFINITY } }).data;
  const trials = (useMeasurementsOverview(ev ?? 0, { query: { enabled: !!ev } }).data ?? []).filter((o) => o.register_plots > 0);
  const { pedigree, valid: validOnly, unlinked: onlyUnlinked } = params;
  const [adding, setAdding] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);
  const client = useQueryClient();
  const canEdit = hasRole(useUserMe().data?.roles, "nemesito");

  // the chosen properties are the user's (kept with their settings), the legacy "Alap lista" until they choose their own
  const chosenFields = params.fields ?? settings?.history_fields ?? [];
  const custom = chosenFields.length > 0;
  const fields = custom ? chosenFields : (presets?.base_list ?? []);
  const setFields = (next: string[]) => {
    saveSetting("history_fields", next);
    void set({ fields: next });
  };
  const labelOf = useMemo(() => new Map((presets?.fields ?? []).map((f) => [f.id, f.label])), [presets]);
  const choices = useMemo(() => {
    const chosen = new Set(fields);
    return (presets?.fields ?? []).filter((f) => !chosen.has(f.id)).map((f) => ({ value: f.id, label: `${f.table_label} · ${f.label}` }));
  }, [presets, fields]);
  const maxDepth = presets?.max_depth[params.method] ?? 1;
  const depth = Math.min(params.depth, maxDepth);
  const shown = new Set([...fields, params.trait]);
  const fieldSites = Object.fromEntries(Object.entries(params.field_sites).filter(([f]) => shown.has(f)));
  const body: HistoryIn = {
    method: params.method,
    depth,
    fields: [...shown],
    sites: params.th,
    site_mode: params.site_mode,
    trend: params.trait,
    field_sites: fieldSites,
  };
  const setOwnSites = (field: string, own: FieldSitesIn | undefined) => {
    const { [field]: _, ...rest } = params.field_sites;
    void set({ field_sites: own ? { ...rest, [field]: { sites: own.sites ?? [], mode: own.mode ?? "avg" } } : rest });
  };
  const kis = params.kis;
  const history = useQuery({
    queryKey: ["trial-history", ev, kis, body],
    enabled: !!ev && !!kis && !!presets && fields.length > 0,
    queryFn: ({ signal }) => measurementsTrialHistory(ev ?? 0, kis ?? "", body, { signal }),
    placeholderData: keepPreviousData, // a new site choice keeps the table on screen while it loads
  });
  const data = history.data;
  // "only valid" and "only unlinked" are filters of the loaded rows: no new request
  const rows = useMemo(
    () =>
      (data?.rows ?? []).filter(
        (r) => (!onlyUnlinked || r.links[0] === null) && (!validOnly || Object.values(r.values).some((v) => v !== null)),
      ),
    [data, onlyUnlinked, validOnly],
  );
  const table = useHistoryTable(data, rows, pedigree);
  const set = (patch: Partial<z.infer<typeof search>>) => navigate({ search: (s) => ({ ...s, ...patch }) });
  const trial = trials.find((o) => o.kis === kis);
  // a field's own sites: the trial's and every site of the loaded generations (earlier ones may have grown elsewhere)
  const siteChoices = useMemo(() => {
    const loaded = (data?.rows ?? []).flatMap((r) => (r.sources ?? []).flatMap((x) => x.sites));
    return [...new Set([...(trial?.sites ?? []), ...(data?.sites ?? []), ...loaded])].sort();
  }, [trial?.sites, data]);

  return (
    <div className="page fill">
      <PageHeader
        title={t("yearlyRetriever.title")}
        season
        pinSearch={{ fields }}
        subtitle={t("yearlyRetriever.subtitle")}
        item={kis && ev ? { kind: "trial", title: `${kis} · ${t("yearlyRetriever.title")}`, subtitle: String(ev) } : undefined}
        actions={
          <ActionMenu
            size="xs"
            actions={[{ label: t("parallelExperiments.button"), onClick: () => setCopying(true), hidden: !canEdit || !ev }]}
          />
        }
      />

      <section className="card pad" style={{ flexShrink: 0 }}>
        <div className="hstack" style={{ gap: 20, alignItems: "flex-end", flexWrap: "wrap" }}>
          <Select
            size="xs"
            w={220}
            label={t("yearlyRetriever.trial")}
            placeholder={t("yearlyRetriever.choosePlaceholder")}
            searchable
            data={trials.map((o) => ({
              value: o.kis,
              label: `${o.kis} · ${o.sites.join(", ")} · ${t("value.plotCount", { count: formatNumber(o.register_plots) })}`,
            }))}
            value={kis ?? null}
            onChange={(v) => v && set({ kis: v })}
          />
          <div className="control-field">
            <Input.Label id="history-method">{t("yearlyRetriever.method")}</Input.Label>
            <SegmentedControl
              aria-labelledby="history-method"
              size="xs"
              value={params.method}
              onChange={(v) => set({ method: v as (typeof METHODS)[number] })}
              data={METHODS.map((m) => ({ value: m, label: t(`yearlyRetriever.methods.${m}`) }))}
            />
            <Input.Description>{t(`yearlyRetriever.methodDescriptions.${params.method}`)}</Input.Description>
          </div>
          <div className="control-field">
            <Input.Label id="history-depth">{t("yearlyRetriever.generationsBack")}</Input.Label>
            <SegmentedControl
              aria-labelledby="history-depth"
              size="xs"
              value={String(depth)}
              onChange={(v) => set({ depth: Number(v) })}
              data={Array.from({ length: maxDepth + 1 }, (_, i) => ({
                value: String(i),
                label: i ? String(i) : t("yearlyRetriever.currentYearOnly"),
              }))}
            />
          </div>
          <div className="control-field">
            <Input.Label id="history-sites">{t("yearlyRetriever.sites")}</Input.Label>
            <SegmentedControl
              aria-labelledby="history-sites"
              size="xs"
              value={params.site_mode}
              onChange={(v) => set({ site_mode: v as (typeof SITE_MODES)[number] })}
              data={SITE_MODES.map((m) => ({ value: m, label: t(`yearlyRetriever.siteMode.${m}`) }))}
            />
          </div>
          <TagsInput
            size="xs"
            w={220}
            label={t("yearlyRetriever.siteFilter")}
            placeholder={t("inputHints.tags")}
            description={t("yearlyRetriever.allSitesLabel")}
            value={params.th}
            data={trial?.sites}
            onChange={(v) => set({ th: v.map((s) => s.trim().toUpperCase()).filter(Boolean) })}
          />
        </div>
      </section>

      {/* the properties are the table's columns: shown with the table */}
      {params.view === "table" && (
        <section className="card pad stack" style={{ gap: 8, flexShrink: 0 }}>
          <Input.Label>
            {t(custom ? "yearlyRetriever.traitsCustom" : "yearlyRetriever.traitsDefault", { count: fields.length })}
          </Input.Label>
          <div className="hstack" style={{ gap: 5, flexWrap: "wrap" }}>
            {fields.map((f) => (
              <FieldSitesPill
                key={f}
                field={f}
                label={labelOf.get(f) ?? f}
                sites={siteChoices}
                pageMode={params.site_mode}
                own={params.field_sites[f]}
                onChange={(own) => setOwnSites(f, own)}
                onRemove={() => setFields(fields.filter((x) => x !== f))}
              />
            ))}
            <Select
              size="xs"
              w={240}
              searchable
              limit={60}
              placeholder={t("yearlyRetriever.addTrait")}
              aria-label={t("yearlyRetriever.addTrait")}
              data={choices}
              value={adding}
              onChange={(v) => {
                if (v) setFields([...fields, v]);
                setAdding(null);
              }}
            />
            {custom ? (
              <Button size="compact-xs" variant="subtle" onClick={() => setFields([])}>
                {t("yearlyRetriever.backToDefaultList")}
              </Button>
            ) : null}
          </div>
        </section>
      )}

      <section className="card stack grow history-results" style={{ gap: 0, minHeight: 0, overflow: "hidden" }}>
        <div
          className="hstack history-toolbar"
          style={{ gap: 10, padding: "var(--b-row-padding, 10px 14px)", borderBottom: "1px solid var(--b-border)", flexWrap: "wrap" }}
        >
          <SegmentedControl
            size="xs"
            value={params.view}
            onChange={(v) => set({ view: v as "overview" | "table" })}
            data={[
              { value: "overview", label: t("yearlyRetriever.view.overview") },
              { value: "table", label: t("yearlyRetriever.view.table") },
            ]}
          />
          {params.view === "overview" && (
            <>
              <Select
                size="xs"
                aria-label={t("yearlyRetriever.heatmapTrait")}
                data={traits.map((x) => ({ value: x.id, label: `${x.label} · ${x.unit ?? t("yearlyRetriever.fixedScale")}` }))}
                value={params.trait}
                onChange={(v) => v && set({ trait: v })}
              />
              <Checkbox
                label={t("yearlyRetriever.relativeComparison")}
                checked={params.relative}
                onChange={(e) => set({ relative: e.currentTarget.checked })}
              />
            </>
          )}
          {data && depth > 0 && (
            <>
              <span className="chip g">
                {t("yearlyRetriever.linkedCount", { count: formatNumber(data.linked), total: formatNumber(data.linked + data.unlinked) })}
              </span>
              {data.unlinked > 0 && (
                <button type="button" className={`chip y${onlyUnlinked ? " on" : ""}`} onClick={() => set({ unlinked: !onlyUnlinked })}>
                  {t("yearlyRetriever.unlinkedCount", { count: formatNumber(data.unlinked) })} ›
                </button>
              )}
            </>
          )}
          {history.isFetching && <Loader size="xs" />}
          <div className="grow" />
          {params.view === "table" && (
            <Checkbox
              size="xs"
              label={t("yearlyRetriever.showPedigree")}
              checked={pedigree}
              onChange={(e) => set({ pedigree: e.currentTarget.checked })}
            />
          )}
          <Checkbox
            size="xs"
            label={t("yearlyRetriever.validOnly")}
            checked={validOnly}
            onChange={(e) => set({ valid: e.currentTarget.checked })}
          />
        </div>
        {!kis ? (
          <p className="mu sm" style={{ padding: 16 }}>
            {t("yearlyRetriever.chooseHelp")}
          </p>
        ) : history.error ? (
          <p className="sm" style={{ padding: 16, color: "var(--b-red)" }}>
            {errorMessage(history.error)}
          </p>
        ) : data ? (
          data.rows.length ? (
            params.view === "table" ? (
              <HistoryTable table={table} />
            ) : (
              <HistoryVisual
                key={`${ev}/${kis}/${params.trait}/${params.site_mode}/${params.valid}/${params.unlinked}`}
                data={data}
                rows={rows}
                trait={params.trait}
                relative={params.relative}
                scope={`${ev} · ${kis} · ${params.th.join(", ") || t("yearlyRetriever.allSitesLabel")}`}
              />
            )
          ) : (
            <p className="mu sm" style={{ padding: 16 }}>
              {t("yearlyRetriever.empty")}
            </p>
          )
        ) : (
          <Loader size="sm" m="md" />
        )}
      </section>
      {copying && ev && (
        <ParallelCopyModal
          ev={ev}
          kis={kis}
          defaultSite={settings?.default_th ?? "MV"}
          onClose={() => setCopying(false)}
          onDone={() => {
            setCopying(false);
            void client.invalidateQueries({ queryKey: ["trial-history"] });
          }}
        />
      )}
    </div>
  );
}
